import type { Dialog as BaseDialog } from '@base-ui/react';
import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';

import type { ForumPost, ForumAuthor as TForumAuthor } from '~/api/forum/types';

import { Card, Icon, RichTextRenderer, Separator, Tag } from '~/components';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { clsx } from '~/styles/utils';

import { illustrationForForumTag } from '../Forum.utils';
import { ForumAuthor } from './ForumAuthor';
import {
  actionsStyle,
  cardLinkStyle,
  cardStyle,
  commentsStyle,
  contentStyle,
  footerActionsStyle,
  footerStyle,
  headerStyle,
  onTopStyle,
  pinnedStyle,
  titleStyle,
} from './ForumPostPreview.css';
import { LikeForumPostButton } from './LikeForumPostButton';
import { SaveForumPostButton } from './SaveForumPostButton';

type Props = ComponentPropsWithRef<'div'> & {
  authorDialogHandle: BaseDialog.Handle<TForumAuthor>;
  /** The route the post detail's back link returns to. */
  from?: string;
  post: ForumPost;
  showPinned?: boolean;
};

export const ForumPostPreview = ({ authorDialogHandle, from, post, showPinned = true, ...delegated }: Props) => {
  const { t } = useTranslation(['forum']);

  return (
    <Card.Root {...delegated} className={clsx(cardStyle, delegated.className)}>
      <Card.Link aria-label={post.title} className={cardLinkStyle} state={from && { from }} to={r(routes.forumPost, { postId: post.id })} />

      <header className={headerStyle}>
        <ForumAuthor
          author={post.author}
          className={onTopStyle}
          createdAt={post.createdAt}
          dialogHandle={authorDialogHandle}
          editedAt={post.editedAt}
        />

        <div className={actionsStyle}>
          <Tag illustration={illustrationForForumTag(post.tag)} text={t(`forum:tags.${post.tag}.name`)} />
        </div>
      </header>

      <div className={titleStyle}>{post.title}</div>

      <div className={contentStyle}>
        <RichTextRenderer content={post.content} />
      </div>

      <Separator />

      <footer className={footerStyle}>
        {post.pinned && showPinned && (
          <div className={pinnedStyle}>
            <Icon name="Keep" />

            <span>{t('forum:overview.postPreview.pinned')}</span>
          </div>
        )}

        <div className={footerActionsStyle}>
          <LikeForumPostButton likes={post.likes} postId={post.id} />

          <div className={commentsStyle}>
            <Icon aria-label={t('forum:post.card.actions.comments', { count: post.numberOfComments })} name="ComicBubble" />

            <span aria-hidden>{post.numberOfComments}</span>
          </div>

          <div className={onTopStyle}>
            <SaveForumPostButton postId={post.id} saved={post.saved} />
          </div>
        </div>
      </footer>
    </Card.Root>
  );
};
